import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { useCallback, useMemo, useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATE } from "@/customer/constants";
import { useGetCustomer, useOptOutCustomer } from "@/customer/query";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { useGetCustomerTextMessages } from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";
import { OptOutConfirmationDialog } from "./opt-out-confirmation-dialog";
import { TextMessageAttribution } from "./text-message-attribution";

interface DropdownAction {
  label: string;
  onClick: () => void;
  variant?: "default" | "destructive";
}

export const TextMessageSidebarHeader = ({
  selectedItem,
  setIsSidebarOpen,
}: {
  selectedItem: IMostRecentTextMessage | null;
  setIsSidebarOpen: (open: boolean) => void;
}) => {
  const { businessId } = useBusiness();
  const { data: customer, isLoading } = useGetCustomer(
    selectedItem?.customer.id,
  );
  const { data: messages } = useGetCustomerTextMessages(
    selectedItem?.customer.id,
  );
  const { mutate: optOutCustomer, isPending: isOptingOut } =
    useOptOutCustomer(businessId);
  const [isOptOutDialogOpen, setIsOptOutDialogOpen] = useState(false);

  const oldestTextMessage = {
    ...[...(messages || [])].sort((a, b) =>
      dayjs(a.created_at).diff(dayjs(b.created_at)),
    )[0],
    customer: selectedItem?.customer,
  };
  const signupSource = customer?.signup_source;
  const customerToShow = getCustomerNameOrPhone(selectedItem?.customer);

  const handleOptOut = useCallback(() => {
    if (!customer?.id) return;

    optOutCustomer(customer.id, {
      onSuccess: () => {
        toast.success("Customer opted out of messaging");
        setIsOptOutDialogOpen(false);
        setIsSidebarOpen(false);
      },
      onError: (error) => {
        toast.error("Couldn't opt the customer out of messaging", {
          description: getErrorMessage(error),
        });
      },
    });
  }, [customer?.id, optOutCustomer, setIsSidebarOpen]);

  const isOptedOut = customer?.state === CUSTOMER_STATE.opted_out;
  const showOptOut = customer?.id && !isOptedOut;

  const dropdownActions = useMemo(
    () =>
      [
        showOptOut && {
          label: "Opt out of messaging",
          onClick: () => setIsOptOutDialogOpen(true),
          variant: "destructive" as const,
        },
      ].filter(Boolean) as DropdownAction[],
    [showOptOut],
  );

  return (
    <div className="w-full shrink-0">
      <SimpleSidebarHeader
        className="px-6 pb-3"
        title={customerToShow}
        description={
          customerToShow === customer?.name
            ? formatPhoneNumber(customer?.phone_number)
            : undefined
        }
        actions={
          dropdownActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="More actions"
                    className="ml-auto shrink-0"
                  />
                }
              >
                <DotsThreeVerticalIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-max">
                {dropdownActions.map((action, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={action.onClick}
                    variant={action.variant}
                  >
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
        onClose={() => setIsSidebarOpen(false)}
      />
      <Stack align="start" gap={2} direction="row" className="w-full px-6 pb-4">
        <TextMessageAttribution
          signupSource={signupSource}
          messageBody={oldestTextMessage?.body || ""}
          isLoading={isLoading}
        />
        {isOptedOut && <Badge variant="outline">Opted out</Badge>}
      </Stack>
      <OptOutConfirmationDialog
        open={isOptOutDialogOpen}
        onOpenChange={setIsOptOutDialogOpen}
        customerName={customerToShow}
        isPending={isOptingOut}
        onConfirm={handleOptOut}
      />
    </div>
  );
};

TextMessageSidebarHeader.displayName = "TextMessageSidebarHeader";
